/* ============================================================================
 * reminders.js — daily reminders (migration 115)
 *
 * Morning / lunch / end-of-day reminders for the owner and the crew. The
 * server (cron → daily-reminders edge function) decides what to say and when;
 * this file only turns delivery on and off:
 *   - web push on this device (owner signed in, or crew via portal code)
 *   - a text-message backup with recorded consent
 *   - the account's schedule (settings key `reminders`)
 *
 * Phone sign-ups are SHARED with the live alerts (K.O. Command's kc-push):
 * one key (push_public_key) and one device list, through the existing
 * push_subscribe_owner / push_subscribe(code) / push_unsubscribe RPCs. A
 * browser holds one push key per site, so turning notifications on here
 * covers both.
 * ========================================================================== */

import { supabase } from '@/lib/supabaseClient';
import { fetchSettingsByKey, upsertSettingsByKey } from '@/lib/settingsData';
import { isIosSafari, isStandalone } from '@/lib/pwaRegister';

export const REMINDERS_KEY = 'reminders';

export const DEFAULT_REMINDERS = {
  enabled: true,
  timezone: 'America/Chicago',
  days: [1, 2, 3, 4, 5],
  times: { morning: '07:00', lunch: '12:00', evening: '17:00' },
  owner: true,
  crew: true,
  summary: true,
};

export const fetchReminderSettings = async () => {
  const { value } = await fetchSettingsByKey(REMINDERS_KEY, DEFAULT_REMINDERS);
  const v = value && typeof value === 'object' ? value : {};
  return {
    ...DEFAULT_REMINDERS,
    ...v,
    times: { ...DEFAULT_REMINDERS.times, ...(v.times || {}) },
    days: Array.isArray(v.days) ? v.days : DEFAULT_REMINDERS.days,
  };
};

export const saveReminderSettings = (value) => upsertSettingsByKey(REMINDERS_KEY, value);

export const fetchReminderOverview = async () => {
  const { data, error } = await supabase.rpc('reminders_overview');
  if (error) throw error;
  return data;
};

export const setOwnerSms = async (phone, on) => {
  const { data, error } = await supabase.rpc('reminders_owner_sms', { p_phone: phone || null, p_on: !!on });
  if (error) throw error;
  return data;
};

/* ---------- Web push on this device -------------------------------------- */

/** 'ok' | 'unsupported' | 'ios_install' (iPhone: add to Home Screen first) */
export const pushSupport = () => {
  if (typeof window === 'undefined') return 'unsupported';
  // iPhone/iPad: only an app added to the Home Screen can get notifications.
  const ios = isIosSafari() || /iPad|iPhone|iPod/.test(navigator.userAgent || '');
  if (ios && !isStandalone()) return 'ios_install';
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return 'unsupported';
  }
  return 'ok';
};

const b64urlToBytes = (s) => {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const raw = atob((s + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

const registration = async () => {
  const reg = await navigator.serviceWorker.getRegistration();
  if (reg) return reg;
  // Dev builds don't register the worker on their own (pwaRegister.js).
  return navigator.serviceWorker.register('/service-worker.js', { scope: '/' });
};

export const currentPushSubscription = async () => {
  if (pushSupport() !== 'ok') return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    return reg ? await reg.pushManager.getSubscription() : null;
  } catch (_) {
    return null;
  }
};

export const fetchPushPublicKey = async () => {
  const { data, error } = await supabase.rpc('push_public_key');
  if (error) throw error;
  return data || null;
};

/**
 * Ask for permission and subscribe this device. `save` stores it server-side
 * (owner vs crew portal). Returns { ok, error? }.
 */
export const enablePushOnThisDevice = async (publicKey, save) => {
  const support = pushSupport();
  if (support !== 'ok') return { ok: false, error: support };
  if (!publicKey) return { ok: false, error: 'not_ready' };
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, error: 'denied' };
  const reg = await registration();
  await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  // A sign-up made with a different key can't be reused: start over.
  const key = b64urlToBytes(publicKey);
  const had = sub?.options?.applicationServerKey ? new Uint8Array(sub.options.applicationServerKey) : null;
  if (sub && had && (had.length !== key.length || had.some((b, i) => b !== key[i]))) {
    await sub.unsubscribe().catch(() => {});
    sub = null;
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  }
  const j = sub.toJSON();
  const result = await save({
    endpoint: j.endpoint,
    keys: { p256dh: j.keys?.p256dh, auth: j.keys?.auth },
    user_agent: (navigator.userAgent || '').slice(0, 300),
  });
  if (!result?.ok) return { ok: false, error: result?.error || 'save_failed' };
  return { ok: true };
};

export const disablePushOnThisDevice = async () => {
  const sub = await currentPushSubscription();
  if (!sub) return { ok: true };
  try {
    await supabase.rpc('push_unsubscribe', { p_endpoint: sub.endpoint });
  } finally {
    await sub.unsubscribe().catch(() => {});
  }
  return { ok: true };
};

export const saveOwnerDevice = async (subscription) => {
  const { data, error } = await supabase.rpc('push_subscribe_owner', { p_subscription: subscription });
  if (error) throw error;
  return data;
};

/* ---------- Crew portal (portal code, no login) -------------------------- */

export const fetchCrewReminderStatus = async (code) => {
  const { data, error } = await supabase.rpc('crew_portal_reminder_status', { p_code: code });
  if (error) throw error;
  return data;
};

export const saveCrewDevice = (code) => async (subscription) => {
  const { data, error } = await supabase.rpc('push_subscribe', { p_code: code, p_subscription: subscription });
  if (error) throw error;
  return data;
};

export const setCrewSms = async (code, phone, on) => {
  const { data, error } = await supabase.rpc('crew_portal_sms_optin', {
    p_code: code, p_phone: phone || null, p_on: !!on,
  });
  if (error) throw error;
  return data;
};

/* ---------- Crew home-screen app ------------------------------------------
 * The manifest starts the installed app at "/", which for a crew member with
 * no login means the sign-in page. A crew member who set up reminders here
 * gets sent back to their portal instead (App.js RootRedirect). Cleared on
 * Exit. */
const CREW_HOME_KEY = 'ko_crew_home_code';

export const rememberCrewHome = (code) => {
  try {
    if (/^\d{6}$/.test(String(code || ''))) localStorage.setItem(CREW_HOME_KEY, String(code));
  } catch (_) {
    /* private mode */
  }
};

export const crewHomeCode = () => {
  try {
    const v = localStorage.getItem(CREW_HOME_KEY);
    return /^\d{6}$/.test(v || '') ? v : null;
  } catch (_) {
    return null;
  }
};

export const forgetCrewHome = () => {
  try {
    localStorage.removeItem(CREW_HOME_KEY);
  } catch (_) {
    /* private mode */
  }
};

/* ---------- End-of-day summary (migration 117) ----------------------------
 * The owner's day: labor, materials, rebates, needs attention, good news.
 * `day` is 'YYYY-MM-DD' in the account's time zone, or null for today. */
export const fetchDaySummary = async (day = null) => {
  const { data, error } = await supabase.rpc('owner_day_summary_get', { p_day: day });
  if (error) throw error;
  return data;
};

/** The summary's email copy: settings `daily_recap` {enabled, to}. */
export const fetchSummaryEmail = async () => {
  const { value } = await fetchSettingsByKey('daily_recap', null);
  return { enabled: !!value?.enabled, to: value?.to || '' };
};

export const setSummaryEmail = async (on, to) => {
  const { data, error } = await supabase.rpc('owner_summary_email', { p_on: !!on, p_to: to || null });
  if (error) throw error;
  return data;
};
